import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  ModalRoot,
  ModalBackdrop,
  ModalPositioner,
  ModalContent,
  ModalTitle,
} from '@rcw/ui';
import { useTranslation } from 'i18n/LocaleContext';
import { useRemoveRewardMutation } from 'app/api/gatewayApiSlice';

function CloseIcon() {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none'>
      <path stroke='#39a9f7' strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10M15 9l-6 6M9 9l6 6' />
    </svg>
  );
}

export function RewardRemovalDialog({
  isOpen,
  onOpenChange,
  product,
  idToken,
  username,
  onRemoved,
}) {
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [removeReward, { isLoading }] = useRemoveRewardMutation();

  const handleClose = () => {
    onOpenChange({ open: false });
    setTimeout(() => {
      setError(null);
    }, 200);
  };

  const handleConfirm = async () => {
    if (!product) return;
    setError(null);
    try {
      const res = await removeReward({
        username,
        productId: product.id,
        idToken,
      }).unwrap();

      if (res?.isSuccess === false) {
        setError(res.message || t('RVS.COMPONENT.LOYALTY_PROGRAM.ERROR_REMOVING', 'Failed to remove instrument.'));
        return;
      }

      if (onRemoved) onRemoved();
      handleClose();
    } catch (err) {
      console.error('[RewardRemovalDialog] Error:', err);
      setError(err?.data?.message || err?.message || t('RVS.COMPONENT.LOYALTY_PROGRAM.ERROR_REMOVING', 'An unexpected error occurred.'));
    }
  };

  return (
    <ModalRoot
      open={isOpen}
      onOpenChange={onOpenChange}
    >
      <ModalBackdrop className='!bg-black/80' />
      <ModalPositioner className='!p-0'>
        <ModalContent className='!max-w-lg !rounded-3xl !shadow-2xl md:p-8 relative mx-2 bg-white text-black text-sm'>
          <div className='relative'>
            <button
              type='button'
              onClick={handleClose}
              className='absolute top-5 right-4 md:top-0 md:right-0 p-1 md:p-0 hover:bg-black/5 rounded-full transition-colors'
              aria-label='Close Dialog'
            >
              <CloseIcon />
            </button>
            <div className='px-6 pt-6'>
              <ModalTitle className='!text-2xl !text-black'>
                {t('RVS.COMPONENT.LOYALTY_PROGRAM.REMOVE_INSTRUMENT_TITLE', 'Remove Instrument')}
              </ModalTitle>
            </div>
          </div>

          <div className='px-6 py-6 space-y-6'>
            {error && (
              <div className='bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm'>
                {error}
              </div>
            )}

            <p className='text-gray-600'>
              {t('RVS.COMPONENT.LOYALTY_PROGRAM.REMOVE_INSTRUMENT_DESCRIPTION', 'Are you sure you want to remove this instrument?')}
              {product && (
                <>
                  <br />
                  <strong className='text-roland-blue'>{product.title}</strong>
                </>
              )}
            </p>

            <div className='flex flex-col md:flex-row justify-end gap-4 pt-4'>
              <button
                type='button'
                onClick={handleClose}
                className='btn-secondary order-2 md:order-1'
                disabled={isLoading}
              >
                {t('RVS.COMPONENT.LOYALTY_PROGRAM.BUTTON.CANCEL', 'Cancel')}
              </button>
              <button
                type='button'
                onClick={handleConfirm}
                className='btn-destructive order-1 md:order-2 flex items-center justify-center min-w-[120px]'
                disabled={isLoading}
                aria-label={t('RVS.COMPONENT.LOYALTY_PROGRAM.BUTTON.REMOVE', 'Remove')}
              >
                {isLoading ? (
                  <span className='w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin' />
                ) : (
                  t('RVS.COMPONENT.LOYALTY_PROGRAM.BUTTON.REMOVE', 'Remove')
                )}
              </button>
            </div>
          </div>
        </ModalContent>
      </ModalPositioner>
    </ModalRoot>
  );
}

RewardRemovalDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  product: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
  }),
  idToken: PropTypes.string.isRequired,
  username: PropTypes.string.isRequired,
  onRemoved: PropTypes.func,
};
